/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      lib util (pure) — the confirmed-booking recap and its
 *              «Aggiungi al calendario» .ics file
 *   extends:   -
 *   template:  none
 *   args:      -
 *   uses:      moment-timezone
 *   siblings:  booking-request.js, booking-validation.js (other controllers/index.js extracts)
 *   users:     controllers/index.js (confirmBookingTask snapshots buildBookedRecap,
 *              the downloadIcs action calls downloadBookingIcs), templates/index.hbs +
 *              components/booking-index-nuova.hbs (the «done» card reads the recap)
 *   tests:     tests/unit/lib/booking-ics-test.js
 *   gotchas:   the recap is a SNAPSHOT taken when the server accepted the
 *              booking: confirmBookingTask clears `people` / `dispos_slot` right
 *              after, and a later total recalculation must not change what the
 *              customer was told. The .ics is generated client-side from it:
 *              no request, no booking reference (the free-booking reply carries
 *              only `id: 0`). Times go out in UTC (`Z`), so the calendar app
 *              shows them in the customer's own zone.
 *
 * buildBookedRecap(): what the «done» card repeats (service, operator, date,
 * time, place, amount). buildBookingIcs(): an RFC 5545 VCALENDAR with one
 * VEVENT for that recap. icsFileName(): the download name. downloadBookingIcs():
 * hands the file to the browser (blob link, no request).
 */
import moment from 'moment-timezone';

// Plain text out of a product name that may carry markup (it is rendered with
// safe-string in the page): tags dropped, the common entities decoded.
export function plainText(value) {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

// Snapshot of the booking the server just accepted. `src` is the index controller (or any object with
// the same names); `voucherToUse` is the application controller's voucher in use, if any.
export function buildBookedRecap(src = {}, voucherToUse = src.voucherToUse) {
  const { productSelected, voucherToBuy, contactSelected, hourSelected, totalAPI, selectedTimeZone } = src;
  const hasSlot = !voucherToBuy && Boolean(hourSelected?.start);
  const finalPrice = Number(totalAPI?.prezzo_finale) || 0;

  return {
    service: (voucherToBuy ? voucherToBuy.name : productSelected?.name) || null,
    productId: productSelected?.id ?? null,
    operator: contactSelected?.name || null,
    contactId: contactSelected?.id ?? null,
    start: hasSlot ? hourSelected.start : null,
    end: hasSlot ? hourSelected.end || null : null,
    timeZone: selectedTimeZone || null,
    place: hasSlot ? hourSelected.stanza || null : null,
    online: Boolean(productSelected?.teleconsulto),
    withVoucher: Boolean(voucherToUse),
    // the amount left to pay (on site: a booking with an online payment never reaches the «done» card)
    amountDue: !voucherToUse && finalPrice > 0 ? finalPrice : null,
    free: !voucherToUse && Boolean(totalAPI) && !Number(totalAPI?.prezzo_totale),
  };
}

// RFC 5545 §3.3.11 TEXT escaping.
function escapeText(value) {
  return String(value)
    .replace(/\\/g, '\\\\')
    .replace(/\r?\n/g, '\\n')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,');
}

// RFC 5545 §3.1: lines longer than 75 octets are folded (CRLF + one space). Counted in UTF-8 octets and cut
// between characters, so an accented name never splits a code point.
const octets = (ch) => {
  const code = ch.codePointAt(0);
  if (code < 0x80) return 1;
  if (code < 0x800) return 2;
  return code < 0x10000 ? 3 : 4;
};

function fold(line) {
  const parts = [];
  let current = '';
  let size = 0;
  let limit = 75;
  for (const ch of line) {
    const n = octets(ch);
    if (size + n > limit) {
      parts.push(current);
      current = '';
      size = 0;
      limit = 74; // the continuation line starts with one space
    }
    current += ch;
    size += n;
  }
  parts.push(current);
  return parts.join('\r\n ');
}

const utc = (value) => moment(value).utc().format('YYYYMMDD[T]HHmmss[Z]');

// The .ics text for the recap; null when the booking has no slot (a voucher purchase).
export function buildBookingIcs(recap, { organizationName = null, onlineLabel = null, now = new Date() } = {}) {
  if (!recap?.start || !moment(recap.start).isValid()) return null;

  const start = moment(recap.start);
  const end = recap.end && moment(recap.end).isValid() ? moment(recap.end) : start.clone().add(1, 'hour');
  const service = plainText(recap.service);
  const organization = plainText(organizationName);
  const summary = [service, organization].filter(Boolean).join(' · ') || organization || service;
  const location = [recap.place, recap.online ? onlineLabel : null, organization].filter(Boolean).join(', ');
  const description = [recap.operator, recap.online ? onlineLabel : null].filter(Boolean).join(' · ');
  const uid = `booking-${utc(start)}-${recap.productId ?? 'p'}-${recap.contactId ?? 'c'}@beebeeboard`;

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Beebeeboard//Booking//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${utc(now)}`,
    `DTSTART:${utc(start)}`,
    `DTEND:${utc(end)}`,
    `SUMMARY:${escapeText(summary)}`,
  ];
  if (location) lines.push(`LOCATION:${escapeText(location)}`);
  if (description) lines.push(`DESCRIPTION:${escapeText(description)}`);
  lines.push('END:VEVENT', 'END:VCALENDAR');

  return `${lines.map(fold).join('\r\n')}\r\n`;
}

// «prenotazione-2026-10-21.ics»-style name: the date of the booking, ASCII only.
export function icsFileName(recap, prefix = 'booking') {
  const day = recap?.start && moment(recap.start).isValid() ? moment(recap.start).format('YYYY-MM-DD') : 'event';
  const safe = String(prefix || 'booking')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase() || 'booking';
  return `${safe}-${day}.ics`;
}

// «Aggiungi al calendario»: the .ics of the recap, downloaded through a blob link. Returns false when
// there is nothing to download (no slot) or the browser has no blob URLs.
export function downloadBookingIcs(recap, { organizationName = null, onlineLabel = null, fileLabel = 'booking' } = {}) {
  const ics = buildBookingIcs(recap, { organizationName, onlineLabel });
  // silent-guard-ok: the button renders only when the recap has a slot (bookedRecap.start)
  if (!ics || typeof URL?.createObjectURL !== 'function') return false;

  const url = URL.createObjectURL(new Blob([ics], { type: 'text/calendar;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = icsFileName(recap, fileLabel);
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  // revoked later: Safari drops a download whose blob URL is revoked in the same task
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return true;
}
